package org.gymly.ui.admin

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminAccessTerminal
import org.gymly.model.AdminTerminalReconcileAction
import org.gymly.model.AdminTerminalReconcileItem
import org.gymly.model.AdminTerminalReconcileReport
import org.gymly.theme.AdminColors

/**
 * Revisión de usuarios de un terminal: lo que sobra (se quita) y lo que falta (se envía).
 * Nada se aplica sin que el administrador marque la fila y confirme.
 */
@Composable
internal fun TerminalReconcileDialog(terminal: AdminAccessTerminal, onDismiss: () -> Unit) {
    val scope = rememberCoroutineScope()
    var report by remember { mutableStateOf<AdminTerminalReconcileReport?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selected by remember { mutableStateOf(setOf<String>()) }
    var applying by remember { mutableStateOf(false) }
    var confirming by remember { mutableStateOf(false) }
    var resultMsg by remember { mutableStateOf<String?>(null) }

    fun load() {
        scope.launch {
            loading = true
            error = null
            runCatching { AdminRepo.api.reconcileTerminal(terminal.id) }
                .onSuccess {
                    report = it
                    selected = emptySet()
                }
                .onFailure { error = it.message ?: "No se pudo revisar el terminal." }
            loading = false
        }
    }

    LaunchedEffect(terminal.id) { load() }

    fun apply() {
        val current = report ?: return
        val actions = (current.sobran + current.faltan)
            .filter { it.employeeNo in selected }
            .map { AdminTerminalReconcileAction(employeeNo = it.employeeNo, action = it.action) }
        scope.launch {
            applying = true
            confirming = false
            runCatching { AdminRepo.api.applyTerminalReconcile(terminal.id, actions) }
                .onSuccess { res ->
                    resultMsg = "Aplicadas: ${res.applied}." +
                        if (res.errors.isEmpty()) "" else " Con errores: ${res.errors.joinToString("; ")}"
                }
                .onFailure { resultMsg = "No se pudo aplicar: ${it.message}" }
            applying = false
            load()
        }
    }

    AlertDialog(
        onDismissRequest = { if (!applying) onDismiss() },
        title = { Text("Revisar usuarios: ${terminal.name}", fontWeight = androidx.compose.ui.text.font.FontWeight.Bold) },
        text = {
            Column(Modifier.fillMaxWidth().verticalScroll(rememberScrollState())) {
                when {
                    loading -> CircularProgressIndicator(color = AdminColors.Primary)
                    error != null -> Text(error.orEmpty(), color = AdminColors.Error, fontSize = 13.sp)
                    report != null -> {
                        val r = report!!
                        Text(
                            "En el terminal: ${r.onTerminal} · Deberían estar: ${r.expected}",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                        resultMsg?.let {
                            Spacer(Modifier.height(6.dp))
                            Text(it, color = AdminColors.Primary, fontSize = 12.sp)
                        }
                        if (r.sobran.isEmpty() && r.faltan.isEmpty()) {
                            Spacer(Modifier.height(10.dp))
                            Text("El terminal coincide con Gymly.", color = AdminColors.Text, fontSize = 13.sp)
                        }
                        if (r.sobran.isNotEmpty()) {
                            Spacer(Modifier.height(10.dp))
                            Text("Sobran en el terminal (se quitan)", fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold, fontSize = 13.sp)
                            r.sobran.forEach { item ->
                                ReconcileRow(item, selected.contains(item.employeeNo)) { checked ->
                                    selected = toggle(selected, item.employeeNo, checked)
                                }
                            }
                        }
                        if (r.faltan.isNotEmpty()) {
                            Spacer(Modifier.height(10.dp))
                            Text("Faltan en el terminal (se envían)", fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold, fontSize = 13.sp)
                            r.faltan.forEach { item ->
                                ReconcileRow(item, selected.contains(item.employeeNo)) { checked ->
                                    selected = toggle(selected, item.employeeNo, checked)
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { confirming = true },
                enabled = selected.isNotEmpty() && !applying && !loading,
            ) {
                Text("Aplicar seleccionados (${selected.size})")
            }
        },
        dismissButton = {
            Row {
                TextButton(onClick = { load() }, enabled = !loading && !applying) { Text("Actualizar") }
                TextButton(onClick = onDismiss, enabled = !applying) { Text("Cerrar") }
            }
        },
    )

    if (confirming) {
        val count = selected.size
        AlertDialog(
            onDismissRequest = { confirming = false },
            title = { Text("Confirmar cambios") },
            text = { Text("Se aplicarán $count acciones en ${terminal.name}. Las personas que se quitan dejan de poder entrar por ese terminal.") },
            confirmButton = { Button(onClick = { apply() }) { Text("Aplicar") } },
            dismissButton = { TextButton(onClick = { confirming = false }) { Text("Cancelar") } },
        )
    }
}

@Composable
private fun ReconcileRow(item: AdminTerminalReconcileItem, checked: Boolean, onChecked: (Boolean) -> Unit) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
        Checkbox(checked = checked, onCheckedChange = onChecked)
        Column(Modifier.weight(1f)) {
            Text(
                item.name.ifBlank { "Sin nombre" },
                color = AdminColors.Text,
                fontSize = 13.sp,
                fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold,
            )
            Text(
                "${kindLabel(item.kind)} · ${item.reason} · ${item.employeeNo.take(8)}…",
                color = AdminColors.TextMuted,
                fontSize = 11.sp,
            )
        }
    }
}

private fun toggle(current: Set<String>, employeeNo: String, checked: Boolean): Set<String> =
    if (checked) current + employeeNo else current - employeeNo

private fun kindLabel(kind: String): String = when (kind) {
    "SOCIO" -> "Socio"
    "TRABAJADOR" -> "Trabajador"
    else -> "Sin ficha"
}
